export const PULL_TO_RELOAD_THRESHOLD_PX = 56;
export const PULL_TO_RELOAD_RESISTANCE = 0.65;
export const PULL_TO_RELOAD_MAX_PX = 96;
const SCROLL_TOP_EPSILON = 1;

const INSTALLED_DISPLAY_QUERIES = [
  '(display-mode: standalone)',
  '(display-mode: fullscreen)',
  '(display-mode: minimal-ui)',
  '(display-mode: window-controls-overlay)',
];

export const hasTouchInput = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches
    || window.matchMedia('(hover: none)').matches
    || 'ontouchstart' in window;
};

export const isInstalledPwaDisplay = () => {
  if (typeof window === 'undefined') return false;
  if (window.navigator.standalone === true) return true;
  return INSTALLED_DISPLAY_QUERIES.some((query) => window.matchMedia(query).matches);
};

export const isPwaPullToReloadEnabled = () => isInstalledPwaDisplay() && hasTouchInput();

export const subscribeToPullToReloadEnabled = (listener) => {
  if (typeof window === 'undefined') return () => {};

  const media = [
    window.matchMedia('(pointer: coarse)'),
    window.matchMedia('(hover: none)'),
    ...INSTALLED_DISPLAY_QUERIES.map((query) => window.matchMedia(query)),
  ];
  const handleChange = () => listener();
  media.forEach((query) => {
    if (query.addEventListener) {
      query.addEventListener('change', handleChange);
    } else {
      query.addListener(handleChange);
    }
  });

  return () => {
    media.forEach((query) => {
      if (query.removeEventListener) {
        query.removeEventListener('change', handleChange);
      } else {
        query.removeListener(handleChange);
      }
    });
  };
};

export const reloadApp = () => {
  window.location.reload();
};

export const getEventElement = (target) => {
  if (!target) return null;
  if (target.nodeType === 1) return target;
  return target.parentElement || null;
};

export const isEditableTarget = (target) => {
  const element = getEventElement(target);
  if (!element) return false;
  if (element.isContentEditable) return true;
  return Boolean(element.closest('input, textarea, select, [contenteditable="true"]'));
};

const isInertOverlay = (element) => {
  let node = element;
  while (node && node !== document.documentElement) {
    if (node.getAttribute?.('aria-hidden') === 'true') {
      return true;
    }
    if (node.classList?.contains('MuiModal-hidden')) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
};

export const isBlockingOverlayOpen = (root = document) => {
  const overlays = root.querySelectorAll('.MuiModal-root, .MuiDrawer-modal, [aria-modal="true"]');
  return Array.from(overlays).some((overlay) => !isInertOverlay(overlay));
};

export const getAppScrollTop = (root = document, view = typeof window === 'undefined' ? undefined : window) => {
  const appRoot = root.getElementById?.('root');
  return Math.max(
    appRoot?.scrollTop || 0,
    root.scrollingElement?.scrollTop || 0,
    root.documentElement?.scrollTop || 0,
    root.body?.scrollTop || 0,
    view?.scrollY || 0,
    view?.pageYOffset || 0,
  );
};

export const canStartPullFromTarget = (target, {
  view = typeof window === 'undefined' ? undefined : window,
  root = document,
} = {}) => {
  if (isEditableTarget(target) || isBlockingOverlayOpen(root)) {
    return false;
  }

  let node = getEventElement(target);
  while (node && node !== root.documentElement && node !== root.body) {
    if (node.scrollTop > SCROLL_TOP_EPSILON) {
      return false;
    }
    node = node.parentElement;
  }

  return getAppScrollTop(root, view) <= SCROLL_TOP_EPSILON;
};

export const resistedPullDistance = (rawDeltaY) => {
  if (rawDeltaY <= 0) return 0;
  return Math.min(rawDeltaY * PULL_TO_RELOAD_RESISTANCE, PULL_TO_RELOAD_MAX_PX);
};
